import React, {useRef, useState} from 'react';
import '../styles/services.css';
import * as emailjs from "@emailjs/browser";
import ContactFormPopup from "./ContactFormPopup";

const Services = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        interest: 'general',
        marketingConsent: false
    });

    const [formErrors, setFormErrors] = useState({});
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formTouched, setFormTouched] = useState({});

    const services = [
        {
            id: 1,
            icon: "content",
            title: "Automated Content Creation",
            description: "Next-Generation Content Creation: Leverage Our Advanced Language Models to Generate Engaging, SEO-Optimized Content Across All Your Platforms.",
            action: "Explore Now",
            label: "Your Platforms",
            placeholder: "Which platforms need a little extra sparkle (or a total makeover)? "
        },
        {
            id: 2,
            icon: "campaign",
            title: "Smart Campaign Management",
            description: "Effortless Ad Optimization: Our AI Assistants Handle Your Campaigns, Maximizing Budget & Targeting.",
            action: "Boost Campaigns",
            label: "Your Platforms",
            placeholder: "Which platforms need a little extra sparkle (or a total makeover)?"
        },
        {
            id: 3,
            icon: "analytics",
            title: "AI-Driven Analytics",
            description: "Real-Time Marketing Optimization: Our AI Continuously Analyzes Market Trends and Customer Behavior to Keep Your Strategy Ahead.",
            action: "Unlock Insights",
            label: "Your Products & Services",
            placeholder: "List your current and future services..."
        },
        {
            id: 4,
            icon: "conversation",
            title: "Conversational Marketing",
            description: "Transform Customer Interactions with AI-Powered Chatbots & Voice Agents: Delivering Seamless, Personalized Experiences.",
            action: "Engage Instantly",
            label: "Your Products & Services",
            placeholder: "List your current and future services..."
        },
        {
            id: 5,
            icon: "unified",
            title: "Omnichannel Integration",
            description: "Drive Results with Unified Marketing: Seamless Campaigns Across Email, Social, Web, and All Your Key Channels.",
            action: "Connect Seamlessly",
            label: "Your Platforms & Website (s)",
            placeholder: "Which platforms and/or website(s) need a little extra sparkle (or a total makeover)? "
        },
        {
            id: 6,
            icon: "audience",
            title: "Audience Segmentation",
            description: "Stop Wasting Marketing Dollars: Data-Driven Identification of Your Most Valuable Niche Segments.",
            action: "Refine Your Reach",
            label: "Your Products & Services",
            placeholder: "List your current and future services..."
        },
        {
            id: 7,
            icon: "website",
            title: "Website Design & Development",
            description: "Ditch Generic Websites: Bold, Data-Driven Designs Crafted at Lightning Speed to Captivate and Convert Your Perfect Audience",
            action: "Design Dynamically",
            label: "Your Products & Services",
            placeholder: "List your current and future services..."
        },
        {
            id: 8,
            icon: "seo",
            title: "Search Engine Optimization",
            description: "Crush the Search Game: Our Cutting-Edge SEO Magic Delivers Stellar Results for Your Best Customers.",
            action: "Optimize Rankings",
            label: "Your Website",
            placeholder: "Give us your website's secret entrance (URL/web address)..."
        },
        {
            id: 9,
            icon: "bootstrap",
            title: "Startup Toolkit",
            description: "Boost Your Startup Toolkit: Exquisite, Data-Driven Business Cards, Flyers, Brochures, Posters, Banners, Social Media Graphics, Email Templates, Catalogs, Stickers, Signage, Postcards, Presentation Folders, Menus, Loyalty Cards and Branded Merchandise Crafted with Unparalleled Speed to Inspire and Attract Your Audience.",
            action: "Launch Boldly",
            label: "Your Products & Services",
            placeholder: "Give an exhaustive list of your services..."
        }
    ];

    const industryOptions = [
        // Professional Services
        {value: 'legal', label: 'Legal'},
        {value: 'accounting', label: 'Accounting & Financial Advisory'},
        {value: 'consulting', label: 'Consulting'},
        {value: 'architecture', label: 'Architecture & Interior Design'},
        {value: 'engineering', label: 'Engineering'},
        {value: 'marketing', label: 'Marketing & Advertising'},
        {value: 'real-estate', label: 'Real Estate'},
        {value: 'insurance', label: 'Insurance'},

        // Healthcare & Wellness
        {value: 'healthcare', label: 'Healthcare'},
        {value: 'dental', label: 'Dental'},
        {value: 'mental-health', label: 'Mental Health & Therapy'},
        {value: 'pharmaceutical', label: 'Pharmaceutical'},
        {value: 'fitness', label: 'Fitness & Gyms'},
        {value: 'nutrition', label: 'Nutrition & Dietetics'},
        {value: 'alternative-medicine', label: 'Alternative Medicine'},
        {value: 'senior-care', label: 'Senior Care'},

        // Technology & IT
        {value: 'software', label: 'Software Development'},
        {value: 'cybersecurity', label: 'Cybersecurity'},
        {value: 'ai-ml', label: 'AI & Machine Learning'},
        {value: 'blockchain', label: 'Blockchain & Cryptocurrency'},
        {value: 'telecom', label: 'Telecommunications'},
        {value: 'cloud', label: 'Cloud Computing'},
        {value: 'it-support', label: 'IT Support'},
        {value: 'hardware', label: 'Hardware Manufacturing'},

        // Retail & E-Commerce
        {value: 'fashion', label: 'Fashion & Apparel'},
        {value: 'jewelry', label: 'Jewelry & Accessories'},
        {value: 'electronics', label: 'Electronics'},
        {value: 'home-goods', label: 'Home Goods & Furniture'},
        {value: 'grocery', label: 'Grocery & Food Delivery'},
        {value: 'books', label: 'Books & Stationery'},
        {value: 'specialty-retail', label: 'Specialty Retail'},
        {value: 'dropshipping', label: 'Dropshipping'},

        // Hospitality & Food
        {value: 'restaurants', label: 'Restaurants & Cafés'},
        {value: 'hotels', label: 'Hotels & Resorts'},
        {value: 'travel', label: 'Travel Agencies'},
        {value: 'events', label: 'Event Planning'},
        {value: 'food-trucks', label: 'Food Trucks'},
        {value: 'beverages', label: 'Breweries & Wineries'},
        {value: 'bakeries', label: 'Bakeries'},

        // Creative & Media
        {value: 'photography', label: 'Photography'},
        {value: 'graphic-design', label: 'Graphic Design'},
        {value: 'music', label: 'Music Production'},
        {value: 'film', label: 'Film & Animation'},
        {value: 'podcasting', label: 'Podcasting'},
        {value: 'publishing', label: 'Publishing'},
        {value: 'gaming', label: 'Gaming & Esports'},

        // Education & Training
        {value: 'education', label: 'Schools & Universities'},
        {value: 'e-learning', label: 'Online Education'},
        {value: 'tutoring', label: 'Tutoring'},
        {value: 'corporate-training', label: 'Corporate Training'},
        {value: 'language', label: 'Language Institutes'},
        {value: 'vocational', label: 'Vocational Training'},
        {value: 'edtech', label: 'Educational Technology'},

        // Manufacturing & Industrial
        {value: 'automotive', label: 'Automotive'},
        {value: 'aerospace', label: 'Aerospace'},
        {value: 'textiles', label: 'Textiles'},
        {value: 'chemicals', label: 'Chemicals'},
        {value: 'construction-materials', label: 'Construction Materials'},
        {value: 'machinery', label: 'Heavy Machinery'},
        {value: 'consumer-goods', label: 'Consumer Goods'},
        {value: '3d-printing', label: '3D Printing'},

        // Non-Profit & Social Impact
        {value: 'environment', label: 'Environmental'},
        {value: 'animal-welfare', label: 'Animal Welfare'},
        {value: 'human-rights', label: 'Human Rights'},
        {value: 'community', label: 'Community Development'},
        {value: 'health-advocacy', label: 'Health Advocacy'},
        {value: 'education-nonprofit', label: 'Education Non-Profit'},
        {value: 'disaster-relief', label: 'Disaster Relief'},

        // Finance & Banking
        {value: 'investment', label: 'Investment'},
        {value: 'fintech', label: 'Fintech'},
        {value: 'accounting-services', label: 'Accounting Services'},
        {value: 'crypto', label: 'Cryptocurrency'},
        {value: 'lending', label: 'Peer-to-Peer Lending'},
        {value: 'financial-planning', label: 'Financial Planning'},

        // Agriculture & Farming
        {value: 'organic-farming', label: 'Organic Farming'},
        {value: 'agritech', label: 'Agritech'},
        {value: 'livestock', label: 'Livestock'},
        {value: 'floriculture', label: 'Floriculture'},
        {value: 'hydroponics', label: 'Hydroponics'},
        {value: 'farm-to-table', label: 'Farm-to-Table'},

        // Government & Public Sector
        {value: 'government', label: 'Government'},
        {value: 'public-safety', label: 'Public Safety'},
        {value: 'transportation', label: 'Transportation'},
        {value: 'utilities', label: 'Utilities'},
        {value: 'defense', label: 'Defense'},

        // Transport & Logistics
        {value: 'transport-logistics', label: 'Transport & Logistics'}
    ];

    const [searchTerm, setSearchTerm] = useState('');
    const [displayList, setDisplayList] = useState(false);
    const [webDesign, setWebDesign] = useState(false);
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedService, setSelectedService] = useState('');
    const [isPopupOpen, setIsPopupOpen] = useState(true);

    const filteredIndustries = industryOptions.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const customerInfoRef = useRef(null);

    const scrollToCustomerInforef = () => {
        customerInfoRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Mark field as touched
        setFormTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prevErrors => ({
                ...prevErrors,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = "Name is required";
        }

        if (!formData.email.trim()) {
            errors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = "Email address is invalid";
        }

        if (!formData.message.trim()) {
            errors.message = "Message is required";
        } else if (formData.message.trim().length < 10) {
            errors.message = "Message must be at least 10 characters";
        }

        if (formData.phone && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(formData.phone)) {
            errors.phone = "Phone number is invalid";
        }

        return errors;
    };

    // Validate on blur
    const handleBlur = (e) => {
        const {name} = e.target;

        setFormTouched(prev => ({
            ...prev,
            [name]: true
        }));

        const fieldErrors = validateField(name, formData[name]);
        if (fieldErrors) {
            setFormErrors(prev => ({
                ...prev,
                [name]: fieldErrors
            }));
        }
    };

    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                return !value.trim() ? "Name is required" : null;
            case 'email':
                if (!value.trim()) return "Email is required";
                if (!/\S+@\S+\.\S+/.test(value)) return "Email address is invalid";
                return null;
            case 'message':
                if (!value.trim()) return "Message is required";
                if (value.trim().length < 10) return "Message must be at least 10 characters";
                return null;
            case 'phone':
                if (value && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(value))
                    return "Phone number is invalid";
                return null;
            default:
                return null;
        }
    };

    const closePopup = () => {
        setWebDesign(false)
    };

    const postSubmit = () => {
        setFormSubmitted(true)
        document.getElementById('pricing').scrollIntoView({behavior: 'smooth'})
    }

    return (
        <section id="services" className="services-section">
            <div className="section-header">
                <h2>Our <span className="highlight">Services</span></h2>
                <p>Comprehensive AI solutions that drive results</p>
            </div>
            <div className="services-grid">
                {services.map(service => (
                    <div id={service.icon} key={service.id} className="service-card">
                        <div className={`service-icon ${service.icon}`}></div>
                        <h3>{service.title}</h3>
                        <p>{service.description}</p>
                        <button className="secondary-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedService(service);
                                    setWebDesign(service.title === "Website Design & Development");
                                    customerInfoRef.current?.scrollIntoView({behavior: 'smooth'});
                                    document.getElementById('data-form') && document.getElementById('data-form').scrollIntoView({behavior: 'smooth'});
                                }}>{service.action}
                        </button>
                    </div>
                ))}
            </div>
            {webDesign ? (<div ref={customerInfoRef} className="web-design-form-container">
                {formSubmitted ? (
                    <div id="form-success" className="form-success">
                        <div className="success-icon">
                            <svg viewBox="0 0 24 24" width="48" height="48">
                                <path fill="currentColor"
                                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                        </div>
                        <h3>A New Journey Has Begun!</h3>
                        <h4>You're one step closer to a future-proof digital experience.</h4>
                        <p>Our Intelligent Assistants are already processing your request and assembling the perfect
                            solutions for
                            you. Lumō, our WhatsApp assistant and Scriptō, our email assistant will be reaching out to you within 24 hours.</p>
                        <h3>While you wait, why not continue exploring?</h3>
                        <p>Discover other cutting-edge services that can elevate your business even
                            further.</p>
                        <span></span>
                        <p>Thank you for choosing Intellommerce AI — where innovation, automation, and creativity
                            converge.</p>
                        <button className="secondary-button"
                                onClick={() => {
                                    setFormSubmitted(false);
                                    setWebDesign(false);
                                    document.getElementById('services').scrollIntoView({behavior: 'smooth'})
                                }}>Continue Scrolling →
                        </button>
                    </div>
                ) : (
                    <ContactFormPopup
                        isOpen={true}
                        onClose={closePopup}
                        selectedServiceId={selectedService.icon}
                        postSubmit={postSubmit}/>)
                }
            </div>) : document.getElementById('contact-form') && document.getElementById('contact-form').scrollIntoView({behavior: 'smooth'})}
        </section>
    );
};

export default Services;